import { NavigationProp, ParamListBase, RouteProp } from '@react-navigation/native';
import { NavigationState } from '@react-navigation/routers';

import { URLS } from '@/data';
import { createJurisdictionHeaders } from '@/data';
import { useIdpvStatusOverrideStore } from '@/feature/devsettings/hooks/use-idpv-status-override-store';
import { useUserStore } from '@/hooks/use-user-store';
import { IdpvSourceType } from '@/navigation/types';
import { isWeb } from '@/utils/constants-platform-specific';
import { useQuery } from '@tanstack/react-query';

type GenerateIdpvUrlResponse = { data: { applicant_id: string; url: string } };

type GetIdpvStatusResponse = {
    data: { status: IdpvStatus };
};

type Navigation = Pick<NavigationProp<ParamListBase>, 'getParent'> & {
    getState(): NavigationState | undefined;
};

export type IdpvStatus = 'activated' | 'created' | 'processing' | 'complete' | 'failed' | 'archived';
export type Route = Pick<RouteProp<ParamListBase>, 'key' | 'name'>;

/**
 * Name of the screen below the given one.
 * For the first screen of a nested stack (e.g. KycStack), it's the screen below the stack itself.
 */
export const getPreviousRouteName = (navigation: Navigation, route: Route): string => {
    const navState = navigation.getState();
    const currentRouteIndex = navState?.routes.findIndex(r => r.key === route.key) ?? -1;

    if (navState && currentRouteIndex > 0) {
        return navState.routes[currentRouteIndex - 1].name;
    }

    const parent = navigation.getParent<Navigation | undefined>();
    const parentState = parent?.getState();
    const stackRoute = parentState?.routes[parentState.index];

    return parent && stackRoute ? getPreviousRouteName(parent, stackRoute) : '';
};

const IDPV_RETURN_HOST = 'betr.app';

export const isIdpvReturnUrl = (url: string): boolean => {
    try {
        // the RN URL polyfill does not lowercase the host
        const host = new URL(url).hostname.toLowerCase();
        return host === IDPV_RETURN_HOST || host.endsWith(`.${IDPV_RETURN_HOST}`);
    } catch {
        return false;
    }
};

export const generateIdpvUrl = async (
    applicantId: string,
    source: IdpvSourceType
): Promise<GenerateIdpvUrlResponse> => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/user/id-comply/document-scan`, {
        method: 'POST',
        headers: {
            ...createJurisdictionHeaders(),
            Authorization: `Bearer ${useUserStore.getState().session?.access_token}`,
            'Content-Type': 'application/json',
            // X-Agent-Source is a custom header used to determine the source of the request (web or mobile)
            'X-Agent-Source': isWeb ? 'web' : 'mobile',
        },
        body: JSON.stringify({ applicant_id: applicantId, source }),
    });
    const data = await response.json();
    if (!response.ok) {
        throw data;
    }
    return data;
};

export const getIdpvStatus = async (applicantId: string): Promise<GetIdpvStatusResponse> => {
    const query = new URLSearchParams();
    query.append('applicant_id', applicantId);
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/user/id-comply/document-scan?${query}`, {
        headers: {
            Authorization: `Bearer ${useUserStore.getState().session?.access_token}`,
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    if (!response.ok) {
        throw data;
    }
    return data;
};

export const idpvStatusKeys = {
    all: ['idpv-status'] as const,
    byApplicantId: (applicantId: string) => [...idpvStatusKeys.all, applicantId] as const,
};

export const useGetIdpvStatus = (applicantId: string) => {
    // Dev-only override set from the Developer screen so QA can force the IDPV outcome
    // without depending on the provider's verdict.
    const statusOverride = useIdpvStatusOverrideStore(state => (state.enabled ? state.status : undefined));
    const query = useQuery({
        queryKey: idpvStatusKeys.byApplicantId(applicantId),
        queryFn: () => getIdpvStatus(applicantId),
        select: data => data.data,
        enabled: !!applicantId,
    });

    if (!statusOverride) {
        return query;
    }
    // Applied even when the real poll errored, so a forced outcome still resolves.
    return { ...query, data: { ...query.data, status: statusOverride } };
};
